import { useState, useEffect, useCallback } from 'react';
import { webhooks } from '../services/api';
import { confirmDelete, showError } from '../services/alerts';
import { IconWebhook } from './icons';

const mask = secret => `${secret.slice(0, 6)}${'•'.repeat(24)}`;

export default function WebhooksPanel() {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [createdHook, setCreatedHook] = useState(null); // shown once, but stays viewable afterward too
  const [revealedIds, setRevealedIds] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const { data } = await webhooks.list();
      setHooks(data);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to load webhooks');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newUrl.trim()) return;
    setCreating(true);
    try {
      const { data } = await webhooks.create(newUrl.trim());
      setCreatedHook(data);
      setNewUrl('');
      load();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to create webhook');
    } finally { setCreating(false); }
  };

  const handleRevoke = async (hook) => {
    if (!(await confirmDelete(hook.url))) return;
    try { await webhooks.revoke(hook.id); load(); }
    catch (err) { showError(err.response?.data?.error || 'Failed to revoke webhook'); }
  };

  const toggleReveal = id => setRevealedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div className="w-full max-w-2xl">
      <div className="mb-5 flex items-center gap-2">
        <IconWebhook />
        <div className="font-display text-base font-bold">Webhooks</div>
      </div>
      <p className="mb-2 text-sm text-muted">
        Get an HTTP POST the moment a project finishes, instead of polling the dashboard.
        Available on paid plans.
      </p>
      <p className="mb-4 text-xs text-dim">
        Verify each delivery with the signing secret below:{' '}
        <code className="block whitespace-pre-wrap rounded bg-surface2 p-2 mt-1">
          {`const expected = 'sha256=' + crypto.createHmac('sha256', SECRET).update(rawBody).digest('hex');
// compare against the X-MailCutover-Signature header using crypto.timingSafeEqual`}
        </code>
      </p>

      <div className="mb-5 flex gap-2">
        <input value={newUrl} onChange={e => setNewUrl(e.target.value)}
          placeholder="https://example.com/webhooks/vanta"
          className="flex-1 rounded-md border border-border2 bg-surface2 px-3 py-2 text-[13px] text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/20" />
        <button onClick={handleCreate} disabled={creating || !newUrl.trim()}
          className="rounded-md bg-accent px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-accent2 disabled:cursor-not-allowed disabled:opacity-70">
          {creating ? 'Creating…' : '+ New Webhook'}
        </button>
      </div>

      {loading ? null : hooks.length === 0 ? (
        <div className="text-sm text-muted">No webhooks yet.</div>
      ) : (
        <div className="divide-y divide-border2 rounded-lg border border-border2">
          {hooks.map(h => (
            <div key={h.id} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-ink">{h.url}</div>
                <div className="flex items-center gap-2 font-mono text-xs text-muted">
                  <span>{revealedIds.has(h.id) ? h.secret : mask(h.secret)}</span>
                  <button onClick={() => toggleReveal(h.id)}
                    className="rounded border border-border2 px-1.5 py-0.5 text-[10px] font-semibold text-dim transition-colors hover:text-ink">
                    {revealedIds.has(h.id) ? 'Hide' : 'Show'}
                  </button>
                  <button onClick={() => navigator.clipboard.writeText(h.secret)}
                    className="rounded border border-border2 px-1.5 py-0.5 text-[10px] font-semibold text-dim transition-colors hover:text-ink">
                    Copy
                  </button>
                </div>
                <div className="text-xs text-dim">
                  Created {new Date(h.created_at).toLocaleDateString()}
                  {h.last_triggered_at ? ` · Last delivered ${new Date(h.last_triggered_at).toLocaleDateString()}` : ' · Never delivered'}
                </div>
              </div>
              <button onClick={() => handleRevoke(h)}
                className="ml-3 shrink-0 rounded-md border border-border2 px-3 py-1.5 text-xs font-semibold text-bad transition-colors hover:border-bad">
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}

      {createdHook && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-border2 bg-surface p-7 shadow-2xl">
            <div className="mb-3 font-display text-lg font-bold text-ink">Webhook created</div>
            <p className="mb-3 text-sm text-warn">
              Copy your signing secret — you can view it anytime from this page.
            </p>
            <div className="mb-4 flex items-center gap-2 rounded-md border border-border2 bg-surface2 px-3 py-2 font-mono text-xs text-ink">
              <span className="flex-1 break-all">{createdHook.secret}</span>
              <button onClick={() => navigator.clipboard.writeText(createdHook.secret)}
                className="shrink-0 rounded-md border border-border2 px-2 py-1 text-[11px] font-semibold text-muted transition-colors hover:text-ink">
                Copy
              </button>
            </div>
            <button onClick={() => setCreatedHook(null)}
              className="w-full rounded-md bg-accent px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-accent2">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
